import { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  MessageSquare,
  Send,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Inbox
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '../../lib/utils';

type MessagingChannel = 'sms' | 'whatsapp' | 'telegram' | 'email';

interface AnalyticsData {
  summary: {
    total_messages: number;
    sent: number;
    received: number;
    delivered: number;
    failed: number;
    delivery_rate: number;
    deliverable_sent: number;
    delivery_basis_channels: string[];
  };
  daily_stats: Array<{
    date: string;
    sent: number;
    received: number;
  }>;
  channel_breakdown: Array<{
    channel: string;
    count: number;
    percentage: number;
  }>;
  identity_breakdown?: Array<{
    identity: string;
    channel: string;
    total: number;
    sent: number;
    received: number;
    percentage: number;
  }>;
}

interface PhoneNumber {
  id: string;
  phone_number: string;
  type: string;
  label?: string;
}

interface EmailAccount {
  id: string;
  email_address: string;
  display_name?: string | null;
  account_type?: string;
}

interface SenderOption {
  value: string;
  label: string;
}

const CHANNEL_META: Record<MessagingChannel, { label: string; color: string }> = {
  sms: { label: 'SMS', color: '#3b82f6' },
  whatsapp: { label: 'WhatsApp', color: '#22c55e' },
  telegram: { label: 'Telegram', color: '#06b6d4' },
  email: { label: 'Email', color: '#f59e0b' }
};

const FALLBACK_COLOR = '#8b5cf6';

function channelLabel(channel: string): string {
  return CHANNEL_META[channel as MessagingChannel]?.label ?? channel;
}

function channelColor(channel: string): string {
  return CHANNEL_META[channel as MessagingChannel]?.color ?? FALLBACK_COLOR;
}

function senderFilterAllLabel(channel: string): string {
  switch (channel) {
    case 'telegram':
      return 'Tutti i bot';
    case 'email':
      return 'Tutti gli account';
    default:
      return 'Tutti i numeri';
  }
}

export default function MessagingAnalytics() {
  const { toast } = useToast();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [senderOptions, setSenderOptions] = useState<SenderOption[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string>('all');
  const [selectedChannel, setSelectedChannel] = useState<string>('all');

  const loadSenders = useCallback(async (channel: string) => {
    if (channel === 'all') {
      setSenderOptions([]);
      return;
    }
    try {
      if (channel === 'email') {
        const response = await apiClient.get<{ data?: EmailAccount[] }>('/email-accounts');
        const accounts = Array.isArray(response.data?.data) ? response.data.data : [];
        setSenderOptions(
          accounts
            .filter((a) => Boolean(a.email_address))
            .map((a) => ({
              value: a.email_address,
              label: a.display_name ? `${a.email_address} (${a.display_name})` : a.email_address
            }))
        );
        return;
      }
      const response = await apiClient.get<{ senders: PhoneNumber[] }>(
        `/messaging/senders?channel=${encodeURIComponent(channel)}`
      );
      const senders = response.success && response.data?.senders ? response.data.senders : [];
      setSenderOptions(
        senders.map((s) => ({
          value: s.phone_number,
          label: s.label ? `${s.phone_number} (${s.label})` : s.phone_number
        }))
      );
    } catch (error) {
      console.error('Failed to load senders:', error);
      setSenderOptions([]);
    }
  }, []);

  useEffect(() => {
    setSelectedPhone('all');
    loadSenders(selectedChannel);
  }, [selectedChannel, loadSenders]);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      const endDate = new Date();
      const startDate = subDays(endDate, days);
      let url = `/messaging/analytics?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`;
      if (selectedPhone && selectedPhone !== 'all') {
        url += `&phone_number=${encodeURIComponent(selectedPhone)}`;
      }
      if (selectedChannel && selectedChannel !== 'all') {
        url += `&channel=${encodeURIComponent(selectedChannel)}`;
      }
      const response = await apiClient.get<AnalyticsData>(url);
      if (response.success && response.data) {
        setAnalytics(response.data);
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile caricare le statistiche',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [period, selectedPhone, selectedChannel, toast]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        <p>Nessun dato disponibile</p>
      </div>
    );
  }

  const { summary } = analytics;
  const channelData = analytics.channel_breakdown.filter((c) => c.count > 0);
  const identityData = analytics.identity_breakdown ?? [];
  const dailyHasData = analytics.daily_stats.some((d) => d.sent > 0 || d.received > 0);
  const deliveryBasisLabel =
    summary.delivery_basis_channels.length > 0
      ? summary.delivery_basis_channels.map(channelLabel).join(', ')
      : '—';

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics Messaggistica</h1>
          <p className="text-muted-foreground">
            Statistiche e trend delle comunicazioni multicanale — SMS, WhatsApp, Telegram ed Email
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedChannel} onValueChange={setSelectedChannel}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Tutti i canali" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i canali</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="telegram">Telegram</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
          {selectedChannel !== 'all' && (
            <Select value={selectedPhone} onValueChange={setSelectedPhone}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={senderFilterAllLabel(selectedChannel)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{senderFilterAllLabel(selectedChannel)}</SelectItem>
                {senderOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30d">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90d">Ultimi 90 giorni</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadAnalytics}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Messaggi Inviati</p>
                <p className="text-3xl font-bold">{formatNumber(summary.sent)}</p>
              </div>
              <Send className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Tutti i canali nel periodo</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Messaggi Ricevuti</p>
                <p className="text-3xl font-bold">{formatNumber(summary.received)}</p>
              </div>
              <Inbox className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Tutti i canali nel periodo</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasso Consegna</p>
                <p className="text-3xl font-bold">{summary.delivery_rate.toFixed(1)}%</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
            </div>
            <div className="mt-4 flex flex-col gap-0.5">
              <span className="text-sm text-muted-foreground">
                {summary.delivered} consegnati su {summary.deliverable_sent} tracciabili
              </span>
              <span className="text-xs text-muted-foreground/80">
                Base: {deliveryBasisLabel}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Falliti</p>
                <p className="text-3xl font-bold">{formatNumber(summary.failed)}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500 opacity-50" />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Solo canali con tracking</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Trend Messaggi</CardTitle>
            <CardDescription>Volume giornaliero Inviati/Ricevuti su tutti i canali</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {dailyHasData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.daily_stats}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => format(new Date(value), 'dd/MM')}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(value) => format(new Date(typeof value === 'string' || typeof value === 'number' ? value : String(value ?? '')), 'dd MMMM yyyy', { locale: it })}
                      contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="sent" name="Inviati" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="received" name="Ricevuti" stroke="#22c55e" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Nessun messaggio nel periodo selezionato
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Canali</CardTitle>
            <CardDescription>Distribuzione per canale</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {channelData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={channelData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="count"
                      nameKey="channel"
                      label={({ name, percent }: { name?: string; percent?: number }) =>
                        `${channelLabel(String(name ?? ''))} ${((percent ?? 0) * 100).toFixed(0)}%`
                      }
                    >
                      {channelData.map((entry) => (
                        <Cell key={`cell-${entry.channel}`} fill={channelColor(entry.channel)} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, _name, item) => [
                        Number(value ?? 0),
                        channelLabel(String((item as { payload?: { channel?: string } })?.payload?.channel ?? ''))
                      ]}
                      contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Nessun dato per i canali selezionati
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {selectedPhone === 'all' && (
        <Card>
          <CardHeader>
            <CardTitle>Per mittente / identità</CardTitle>
            <CardDescription>
              Numero per SMS/WhatsApp, bot per Telegram, account per Email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {identityData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={identityData.map((row) => ({
                      ...row,
                      label: `${row.identity} · ${channelLabel(row.channel)}`
                    }))}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis dataKey="label" type="category" width={180} tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }} />
                    <Legend />
                    <Bar dataKey="sent" name="Inviati" stackId="a" fill="#8b5cf6" />
                    <Bar dataKey="received" name="Ricevuti" stackId="a" fill="#22c55e" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Nessun mittente con messaggi nel periodo
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
